import { useMemo, useState } from "react";

import type { GetMonitoringPageQueryResult } from "@/sanity.types";

export function useComparePlanTableSectionState(
  block: SanityComparePlanTableQueryResult,
) {
  const { views, filterOptions } = useMemo(() => {
    const views = (block.views ?? []).filter(isUsablePlanCompareView);
    return {
      views,
      filterOptions: views.map((view) => ({
        value: view.viewKey,
        label: view.tabLabel,
      })),
    };
  }, [block.views]);

  const [filter, setFilter] = useState(() => views[0]?.viewKey ?? "");

  const activeView = useMemo(
    () => views.find((view) => view.viewKey === filter) ?? views[0],
    [views, filter],
  );

  return {
    filterOptions,
    filter,
    setFilter,
    activeView,
    views,
  };
}

function isUsablePlanCompareView(view: SanityPlanCompareView): boolean {
  const viewKey = view.viewKey?.trim();
  const tabLabel = view.tabLabel?.trim();
  const columns = view.columns ?? [];
  if (!viewKey || !tabLabel || !columns.length) return false;
  if (!columns.every((column) => column.id && column.title && column.price)) {
    return false;
  }
  return true;
}

type MonitoringPageBuilderBlock = NonNullable<
  NonNullable<GetMonitoringPageQueryResult>["pageBuilder"]
>[number];

type ComparePlanTableSectionBlock = Extract<
  MonitoringPageBuilderBlock,
  { _type: "comparePlanTableSection" }
>;

/** GROQ-shaped plan compare view (matches monitoring page builder projection). */
export type SanityPlanCompareView =
  ComparePlanTableSectionBlock["views"][number];

export type SanityPlanCompareColumn = SanityPlanCompareView["columns"][number];

export type SanityPlanCompareCategory =
  SanityPlanCompareView["categories"][number];

export type SanityPlanCompareRow = Exclude<
  SanityPlanCompareCategory["rows"],
  null | undefined
>[number];

/** `title` + `views` + `sectionId` as returned for a compare plan table section. */
export type SanityComparePlanTableQueryResult = Pick<
  ComparePlanTableSectionBlock,
  "title" | "description" | "views"
> & {
  sectionId?: string | null;
};
